import React, { PropsWithChildren, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled, { css } from 'styled-components';

import { Fade } from '@column/column-ui-kit';

export interface PopoverBaseProps {
  show?: boolean;
  className?: string;
  withoutBackdrop?: boolean;
  onClose?: (event: MouseEvent) => void;
  usePortal?: boolean;
  anchorRef?: React.RefObject<HTMLElement>;
  placement?: 'bottom-start' | 'top-start' | 'bottom-end' | 'top-end';
  offset?: number;
}

const Wrapper = styled(Fade)<{ $inlineVars?: Record<string, string> }>`
  --animation-exit-y: -8px;

  position: var(--popover-position, absolute);
  justify-content: center;
  z-index: 5;
  top: var(--popover-top, 16px);
  left: var(--popover-left, 16px);
  right: var(--popover-right, auto);

  ${({ $inlineVars }) =>
    $inlineVars &&
    Object.entries($inlineVars)
      .map(([key, value]) => `${key}: ${value};`)
      .join('\n')}
`;

export const PopoverAction = styled.div`
  display: flex;
  justify-content: space-between;
  background-color: ${({ theme }) => theme.secondary.blendToBackground(25)};
  border-radius: 0 0 12px 12px;
  border-top: 1px solid ${({ theme }) => theme.secondary.blendToBackground(150)};
  padding: 12px 12px 11px 12px;
  margin: 12px 0 0 0;

  ${({ theme }) =>
    theme.id !== 'default' &&
    css`
      background-color: ${theme.body};
      border-top: 1px solid ${theme.secondary.blendToBackground(50)};
    `}
`;

export const StyledPopover = styled.div`
  box-sizing: border-box;
  background-color: ${({ theme }) => theme.background};
  border-radius: 12px;
  box-shadow:
    ${({ theme }) => theme.style.dropdownBorderProperties} ${({ theme }) => theme.style.dropdownBorderWidth}
      ${({ theme }) => theme.style.dropdownBorderColor},
    ${({ theme }) => theme.style.floatingShadow1PropertiesM} ${({ theme }) => theme.style.floatingShadow1ColorM},
    ${({ theme }) => theme.style.floatingShadow2PropertiesM} ${({ theme }) => theme.style.floatingShadow2ColorM};
  padding: var(--popover-padding, 4px);
  width: var(--popover-width, 320px);
  min-width: var(--popover-min-width, 0);
  position: relative;
  z-index: 1;

  ${({ theme }) =>
    theme.id !== 'default' &&
    css`
      box-shadow: 0 0 0 1px ${() => theme.secondary.blendToBackground(150)};
    `}
`;

export const PopoverBase: React.FC<PropsWithChildren<PopoverBaseProps>> = (props) => {
  const baseRef = useRef<HTMLDivElement>(null);
  const [inlineVars, setInlineVars] = useState<Record<string, string>>({});

  const handleDocumentClick = (event: MouseEvent) => {
    if (!(event.target instanceof Element)) {
      return;
    }
    if (
      props.withoutBackdrop ||
      (baseRef.current && (event.target === baseRef.current || baseRef.current.contains(event.target))) ||
      (props.anchorRef?.current &&
        (event.target === props.anchorRef.current || props.anchorRef.current.contains(event.target)))
    ) {
      return;
    }
    if (props.onClose) {
      props.onClose(event);
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleDocumentClick, true);

    return () => document.removeEventListener('click', handleDocumentClick, true);
  }, [handleDocumentClick]);

  useLayoutEffect(() => {
    if (!props.usePortal || !props.show || !props.anchorRef?.current) {
      return;
    }

    const computePosition = () => {
      const anchor = props.anchorRef?.current;
      if (!anchor) {
        return;
      }
      const rect = anchor.getBoundingClientRect();
      const placement = props.placement ?? 'bottom-start';
      const offset = props.offset ?? 8;

      let top = rect.bottom + offset;
      let left = rect.left;

      if (placement === 'top-start') {
        top = rect.top - offset;
        left = rect.left;
      } else if (placement === 'bottom-end') {
        top = rect.bottom + offset;
        left = rect.right;
      } else if (placement === 'top-end') {
        top = rect.top - offset;
        left = rect.right;
      }

      setInlineVars({
        '--popover-top': `${Math.round(top)}px`,
        '--popover-left': `${Math.round(left)}px`,
        '--popover-position': 'fixed',
      });
    };

    computePosition();
    window.addEventListener('resize', computePosition);
    window.addEventListener('scroll', computePosition, true);
    return () => {
      window.removeEventListener('resize', computePosition);
      window.removeEventListener('scroll', computePosition, true);
    };
  }, [props.usePortal, props.show, props.anchorRef?.current, props.placement, props.offset]);

  const content = (
    <Wrapper
      show={props.show ?? false}
      timeoutEnter={0}
      className={props.className}
      $inlineVars={props.usePortal ? inlineVars : undefined}
    >
      <StyledPopover ref={baseRef}>{props.children}</StyledPopover>
    </Wrapper>
  );

  if (props.usePortal) {
    return createPortal(content, document.body);
  }
  return content;
};
